import React, { FC, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { cx } from 'linaria';
import { Carousel } from 'common/components/carousel';
import { useBreakpoints } from 'common/hooks/use-breakpoints';
import { useWindowSize } from '@navi-toolbox/hooks';
import { Skeleton } from 'fronton-react/unstable';
import { getHideServices } from 'features/page-model';
import { areRelatedServicesLoading, getRelatedServices, RELATED_SERVICE_ITEM_CLICK } from './ducks';
import type { RelatedServiceResponse } from './types';
import {
  carousel,
  carouselTablet,
  carouselContainerManyItems,
  titleSkeleton,
  buttonSkeleton,
  SkeletonTitleRow,
  SkeletonRows,
  ServicesSkeletonRow,
  serviceSkeleton,
  carouselContainer,
  moreTwoLinkList,
  linkList,
  RelatedContainer,
} from './styles';
import { RelatedServiceFactory } from './related-service-factory';
import { RelatedServiceButton } from './components/related-service-button';

export const DATA_QA_RELATED_SERVICES = 'related-services_mf-pdp';
export const DATA_QA_RELATED_SERVICES_CAROUSEL = 'related-services-carousel';

type RelatedServicesProps = {
  isVisible?: boolean;
};

export const RelatedServices: FC<RelatedServicesProps> = ({ isVisible }) => {
  const dispatch = useDispatch();
  const { width } = useWindowSize();

  const isHideServices = useSelector(getHideServices);

  let data: RelatedServiceResponse[] = useSelector(getRelatedServices);
  let isLoading = useSelector(areRelatedServicesLoading);

  const service: RelatedServiceResponse | null = data?.length === 1 ? data[0] : null;

  const { mobile, tablet } = useBreakpoints() || { tablet: false };

  function onRelatedServiceClick(payload: RelatedServiceResponse) {
    dispatch(RELATED_SERVICE_ITEM_CLICK(payload));
  }

  const isColumnsTablet = tablet && data.length > 2;

  useEffect(() => {
    if (data?.length > 2) {
      const carouselEl = document.querySelector('[data-qa="related-services-carousel"]');
      const columns = carouselEl?.querySelectorAll('a');
      const firstColumn = carouselEl?.querySelectorAll('a')[0];

      if (firstColumn && columns) {
        columns.forEach((column, index) => {
          if (index) {
            column.style.width = `${firstColumn.clientWidth}px`;
          }
        });
      }
    }
  }, [data.length, mobile, tablet, width]);

  if (!data?.length || isHideServices?.on) {
    return null;
  }

  const isManyItems = data?.length > 2;
  const isSingleItem = data?.length === 1;

  return (
    <RelatedContainer isVisible={isVisible} data-qa={DATA_QA_RELATED_SERVICES}>
      {isLoading ? (
        <SkeletonRows>
          <SkeletonTitleRow>
            <Skeleton className={titleSkeleton} />
            <Skeleton className={buttonSkeleton} />
          </SkeletonTitleRow>
          <ServicesSkeletonRow>
            <Skeleton className={serviceSkeleton} />
            <Skeleton className={serviceSkeleton} />
          </ServicesSkeletonRow>
        </SkeletonRows>
      ) : (
        <>
          <RelatedServiceButton />
          {service ? (
            <RelatedServiceFactory
              service={service}
              onClick={() => onRelatedServiceClick({ ...service, index: 0 })}
              key={service.title}
              href={service.serviceHref}
              isManyItems={isManyItems}
              singleService={isSingleItem}
            />
          ) : (
            <Carousel
              className={cx(carousel, isColumnsTablet ? carouselTablet : null)}
              containerClassName={isManyItems ? carouselContainerManyItems : carouselContainer}
              data-qa={DATA_QA_RELATED_SERVICES_CAROUSEL}
            >
              {data?.map((item, index) => (
                <RelatedServiceFactory
                  vertical
                  key={item.title}
                  service={item}
                  href={item.serviceHref}
                  isManyItems={isManyItems}
                  singleService={isSingleItem}
                  className={cx(isManyItems ? moreTwoLinkList : undefined, linkList)}
                  onClick={() => onRelatedServiceClick({ ...item, index })}
                />
              ))}
            </Carousel>
          )}
        </>
      )}
    </RelatedContainer>
  );
};
